// src/hooks/useFavorites.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const KEY = ['me'];

export const useFavorites = () => {
  const queryClient = useQueryClient();

  const { data: me } = useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const response = await api.get('/users/me');
      return response.data;
    },
  });

  const favorites = me?.favorites ?? [];

  const toggleMutation = useMutation({
    mutationFn: async ({ toolId, next }) => {
      const response = next
        ? await api.post(`/users/me/favorites/${toolId}`)
        : await api.delete(`/users/me/favorites/${toolId}`);
      return response.data;
    },
    // Stars should feel instant, so flip it locally and roll back on failure.
    onMutate: async ({ toolId, next }) => {
      await queryClient.cancelQueries({ queryKey: KEY });
      const previous = queryClient.getQueryData(KEY);

      queryClient.setQueryData(KEY, (old) => {
        if (!old) return old;
        const current = old.favorites ?? [];
        return {
          ...old,
          favorites: next
            ? [...current, toolId]
            : current.filter((id) => id !== toolId),
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(KEY, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
    },
  });

  return {
    favorites,
    isFavorite: (toolId) => favorites.includes(toolId),
    toggleFavorite: (toolId) =>
      toggleMutation.mutate({ toolId, next: !favorites.includes(toolId) }),
  };
};
